<template>
  <a-modal
    :open="open"
    title="결제 카드 관리"
    :footer="null"
    width="700px"
    :destroy-on-close="true"
    @cancel="handleClose"
  >
    <div class="card-management">
      <!-- 등록된 카드 목록 -->
      <div class="card-list-section">
        <div class="section-header">
          <h4>등록된 카드</h4>
          <a-button type="primary" size="small" @click="showAddCardForm = true">
            <template #icon>
              <PlusOutlined />
            </template>
            카드 추가
          </a-button>
        </div>

        <a-spin :spinning="isLoading">
          <div v-if="cardList.length === 0 && !isLoading" class="empty-cards">
            <a-empty description="등록된 카드가 없습니다" />
          </div>

          <div v-else class="card-items">
            <div
              v-for="card in cardList"
              :key="card._id"
              class="card-item"
              :class="{
                'is-inactive': !card.isActive,
                'is-selected': card._id === currentCardId,
                'is-expired': isCardExpired(card.cardExpiry),
              }"
            >
              <div class="card-info">
                <div class="card-number">
                  {{ card.cardNumber || "****-****-****-****" }}
                  <a-tag
                    v-if="isCardExpired(card.cardExpiry)"
                    color="error"
                    class="expired-tag"
                  >
                    <template #icon>
                      <ExclamationCircleOutlined />
                    </template>
                    만료됨
                  </a-tag>
                  <a-tag
                    v-else-if="
                      getCardExpiryStatus(card.cardExpiry).status ===
                      'expiring_soon'
                    "
                    color="warning"
                    class="expiring-soon-tag"
                  >
                    <template #icon>
                      <WarningOutlined />
                    </template>
                    곧 만료
                  </a-tag>
                </div>
                <div class="card-meta">
                  <a-tag v-if="card.cardCompany" color="purple">{{
                    card.cardCompany
                  }}</a-tag>
                  <a-tag
                    v-if="card.ownerType"
                    :color="card.ownerType === '법인' ? 'orange' : 'cyan'"
                  >
                    {{ card.ownerType }}
                  </a-tag>
                  <a-tag v-if="card._id === currentCardId" color="green"
                    >선택됨</a-tag
                  >
                  <span
                    class="card-expiry"
                    :class="{
                      'is-expired-text': isCardExpired(card.cardExpiry),
                    }"
                  >
                    {{ card.cardExpiry }}
                    <span
                      v-if="isCardExpired(card.cardExpiry)"
                      class="expired-notice"
                      >(유효기간 만료)</span
                    >
                  </span>
                  <span v-if="card.cardHolderName" class="card-holder">{{
                    card.cardHolderName
                  }}</span>
                </div>
              </div>
              <div class="card-actions">
                <!-- 만료된 카드는 선택 불가 -->
                <a-tooltip
                  v-if="isCardExpired(card.cardExpiry)"
                  title="만료된 카드는 선택할 수 없습니다"
                >
                  <a-button type="primary" size="small" disabled>
                    선택 불가
                  </a-button>
                </a-tooltip>
                <a-button
                  v-else-if="card._id !== currentCardId && card.isActive"
                  type="primary"
                  size="small"
                  @click="selectCard(card)"
                >
                  선택
                </a-button>
                <a-popconfirm
                  title="이 카드를 삭제하시겠습니까?"
                  ok-text="삭제"
                  cancel-text="취소"
                  @confirm="removeCard(card)"
                >
                  <a-button type="link" size="small" danger> 삭제 </a-button>
                </a-popconfirm>
              </div>
            </div>
          </div>
        </a-spin>
      </div>

      <!-- 카드 추가 폼 -->
      <div v-if="showAddCardForm" class="add-card-section">
        <a-divider>새 카드 등록</a-divider>
        <a-form
          ref="cardFormRef"
          :model="cardForm"
          :rules="cardRules"
          layout="vertical"
        >
          <!-- 카드 유형 선택 -->
          <a-form-item label="카드 유형" name="cardOwnerType">
            <a-radio-group
              v-model:value="cardForm.cardOwnerType"
              @change="handleCardOwnerTypeChange"
            >
              <a-radio value="personal">개인카드</a-radio>
              <a-radio value="corporate">법인카드</a-radio>
            </a-radio-group>
          </a-form-item>

          <a-row :gutter="16">
            <a-col :span="12">
              <a-form-item label="카드번호" name="cardNumber">
                <a-input
                  :value="formattedCardNumber"
                  placeholder="1234-5678-9012-3456"
                  maxlength="19"
                  @input="handleCardNumberInput"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="유효기간 (MM/YY)" name="cardExpiry">
                <a-input
                  v-model:value="cardForm.cardExpiry"
                  placeholder="MM/YY"
                  maxlength="5"
                  @input="handleExpiryInput"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <a-row :gutter="16">
            <a-col :span="12">
              <a-form-item
                :label="
                  cardForm.cardOwnerType === 'corporate'
                    ? '사업자등록번호 (10자리)'
                    : '생년월일 (6자리)'
                "
                name="birthDate"
              >
                <a-input
                  v-model:value="cardForm.birthDate"
                  :placeholder="
                    cardForm.cardOwnerType === 'corporate'
                      ? '123-45-67890'
                      : 'YYMMDD'
                  "
                  :maxlength="cardForm.cardOwnerType === 'corporate' ? 12 : 6"
                  @input="handleBirthDateInput"
                />
                <div
                  v-if="cardForm.cardOwnerType === 'corporate'"
                  class="field-hint"
                >
                  법인카드는 사업자등록번호 10자리를 입력해주세요.
                </div>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="카드 소유자명">
                <a-input
                  v-model:value="cardForm.cardHolderName"
                  placeholder="소유자명 (선택)"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <!-- 보안 안내 문구 -->
          <div class="security-notice">
            <LockOutlined />
            <span>
              카드 정보는 결제 수단 등록 확인 후 즉시 폐기되며, 카드번호는 뒷
              4자리만 암호화하여 저장됩니다.
            </span>
          </div>

          <div class="form-actions">
            <a-button @click="cancelAddCard">취소</a-button>
            <a-button type="primary" :loading="isAdding" @click="addCard">
              {{ isAdding ? "등록 중..." : "카드 등록" }}
            </a-button>
          </div>
          <div
            v-if="validationMessage"
            class="validation-message"
            :class="{
              'is-valid': isCardRegistered,
              'is-error': !isCardRegistered && validationMessage,
            }"
          >
            {{ validationMessage }}
          </div>
        </a-form>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent, ref, computed, watch } from "vue";
import { message } from "ant-design-vue";
import {
  PlusOutlined,
  LockOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons-vue";
import { resellerCompanyCardApi } from "../api";
import { isCardExpired, getCardExpiryStatus } from "../utils/cardExpiry";

export default defineComponent({
  name: "CardManagementModal",
  components: {
    PlusOutlined,
    LockOutlined,
    ExclamationCircleOutlined,
    WarningOutlined,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: String,
      required: true,
    },
    currentCardId: {
      type: String,
      default: "",
    },
    companyBusinessNumber: {
      type: String,
      default: "",
    },
  },
  emits: ["update:open", "updated", "select"],
  setup(props, { emit }) {
    const cardFormRef = ref(null);
    const showAddCardForm = ref(false);
    const isLoading = ref(false);
    const isAdding = ref(false);
    const isCardRegistered = ref(false);
    const validationMessage = ref("");
    const cardList = ref([]);

    const cardForm = ref({
      cardOwnerType: "personal",
      cardNumber: "",
      cardExpiry: "",
      birthDate: "",
      cardHolderName: "",
    });

    // 카드번호 포맷팅 (화면 표시용)
    const formattedCardNumber = computed(() => {
      const num = cardForm.value.cardNumber;
      if (!num) return "";
      return num.match(/.{1,4}/g)?.join("-") || num;
    });

    // 카드번호 입력 핸들러
    const handleCardNumberInput = (e) => {
      const input = e.target;
      const value = input.value.replace(/\D/g, "").slice(0, 16);
      cardForm.value.cardNumber = value;
    };

    const cardRules = {
      cardNumber: [
        { required: true, message: "카드번호를 입력해주세요" },
        {
          validator: (_rule, value) => {
            if (!value || value.length !== 16) {
              return Promise.reject("카드번호 16자리를 입력해주세요");
            }
            return Promise.resolve();
          },
        },
      ],
      cardExpiry: [
        { required: true, message: "유효기간을 입력해주세요" },
        { pattern: /^\d{2}\/\d{2}$/, message: "MM/YY 형식으로 입력해주세요" },
      ],
      birthDate: [
        { required: true, message: "생년월일 또는 사업자번호를 입력해주세요" },
      ],
    };

    const fetchCardList = async () => {
      if (!props.companyId) {
        cardList.value = [];
        return;
      }

      try {
        isLoading.value = true;
        const response = await resellerCompanyCardApi.getByCompany(
          props.companyId
        );
        cardList.value = response.data || [];
      } catch (error) {
        console.error("카드 목록 조회 실패:", error);
        cardList.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    watch(
      () => props.open,
      (newVal) => {
        if (newVal) {
          showAddCardForm.value = false;
          resetCardForm();
          fetchCardList();
        }
      }
    );

    watch(
      () => props.companyId,
      () => {
        if (props.open) {
          fetchCardList();
        }
      }
    );

    const resetCardForm = () => {
      cardForm.value = {
        cardOwnerType: "personal",
        cardNumber: "",
        cardExpiry: "",
        birthDate: "",
        cardHolderName: "",
      };
      cardFormRef.value?.resetFields();
      isCardRegistered.value = false;
      validationMessage.value = "";
    };

    // 카드 유형 변경 핸들러
    const handleCardOwnerTypeChange = () => {
      if (
        cardForm.value.cardOwnerType === "corporate" &&
        props.companyBusinessNumber
      ) {
        const bizNum = props.companyBusinessNumber.replace(/\D/g, "");
        if (bizNum.length === 10) {
          cardForm.value.birthDate = `${bizNum.slice(0, 3)}-${bizNum.slice(
            3,
            5
          )}-${bizNum.slice(5, 10)}`;
        } else {
          cardForm.value.birthDate = props.companyBusinessNumber;
        }
      } else {
        cardForm.value.birthDate = "";
      }
    };

    const handleClose = () => {
      emit("update:open", false);
    };

    const cancelAddCard = () => {
      showAddCardForm.value = false;
      resetCardForm();
    };

    const handleExpiryInput = (e) => {
      const input = e.target;
      let value = input.value.replace(/\D/g, "");
      if (value.length >= 2) {
        value = value.slice(0, 2) + "/" + value.slice(2, 4);
      }
      cardForm.value.cardExpiry = value;
    };

    const handleBirthDateInput = (e) => {
      const input = e.target;
      const value = input.value.replace(/\D/g, "");

      if (value.length <= 6) {
        cardForm.value.birthDate = value;
        return;
      }

      let formatted = value.slice(0, 3);
      if (value.length > 3) {
        formatted += "-" + value.slice(3, 5);
      }
      if (value.length > 5) {
        formatted += "-" + value.slice(5, 10);
      }
      cardForm.value.birthDate = formatted;
    };

    const addCard = async () => {
      if (!props.companyId) {
        message.error("회사 정보가 없습니다.");
        return;
      }

      try {
        await cardFormRef.value?.validate();

        isAdding.value = true;
        validationMessage.value = "";
        isCardRegistered.value = false;

        const cardData = {
          companyId: props.companyId,
          cardNumber: cardForm.value.cardNumber,
          cardExpiry: cardForm.value.cardExpiry,
          birthDate: cardForm.value.birthDate,
          cardHolderName: cardForm.value.cardHolderName || undefined,
        };

        await resellerCompanyCardApi.register(cardData);

        isCardRegistered.value = true;
        validationMessage.value = "카드가 성공적으로 등록되었습니다.";
        message.success("카드가 등록되었습니다.");
        showAddCardForm.value = false;
        resetCardForm();
        await fetchCardList();
        emit("updated");
      } catch (error) {
        console.error("카드 등록 실패:", error);
        isCardRegistered.value = false;
        validationMessage.value =
          error.response?.data?.message ||
          "카드 등록에 실패했습니다. 카드 정보를 확인해주세요.";
        message.error(
          error.response?.data?.message || "카드 등록에 실패했습니다."
        );
      } finally {
        isAdding.value = false;
      }
    };

    const selectCard = (card) => {
      emit("select", card._id);
    };

    const removeCard = async (card) => {
      try {
        await resellerCompanyCardApi.delete(card._id);
        message.success("카드가 삭제되었습니다.");
        await fetchCardList();
        emit("updated");
      } catch (error) {
        console.error("카드 삭제 실패:", error);
        message.error("카드 삭제에 실패했습니다.");
      }
    };

    return {
      cardFormRef,
      showAddCardForm,
      isLoading,
      isAdding,
      isCardRegistered,
      validationMessage,
      cardList,
      cardForm,
      cardRules,
      formattedCardNumber,
      handleClose,
      cancelAddCard,
      handleCardNumberInput,
      handleExpiryInput,
      handleBirthDateInput,
      handleCardOwnerTypeChange,
      addCard,
      selectCard,
      removeCard,
      isCardExpired,
      getCardExpiryStatus,
    };
  },
});
</script>

<style scoped>
.card-management {
  padding: 8px 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.empty-cards {
  padding: 24px 0;
}

.card-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.card-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  transition: all 0.2s ease;
}

.card-item:hover {
  border-color: #d9d9d9;
}

.card-item.is-selected {
  border-color: #52c41a;
  background: #f6ffed;
}

.card-item.is-inactive {
  opacity: 0.6;
}

.card-item.is-expired {
  border-color: #ff4d4f;
  background: #fff2f0;
}

.card-item.is-expired:hover {
  border-color: #ff7875;
}

.card-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.card-number {
  font-size: 16px;
  font-weight: 600;
  font-family: monospace;
  color: #1a1a2e;
}

.card-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.card-expiry {
  color: #666;
  font-size: 13px;
}

.card-expiry.is-expired-text {
  color: #ff4d4f;
  font-weight: 500;
}

.expired-notice {
  color: #ff4d4f;
  font-size: 12px;
  margin-left: 4px;
}

.expired-tag,
.expiring-soon-tag {
  margin-left: 8px;
  font-size: 12px;
}

.card-holder {
  color: #999;
  font-size: 13px;
}

.card-actions {
  display: flex;
  gap: 4px;
  align-items: center;
}

.add-card-section {
  margin-top: 16px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
}

.validation-message {
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 14px;
}

.validation-message.is-valid {
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  color: #52c41a;
}

.validation-message.is-error {
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
  color: #ff4d4f;
}

.field-hint {
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
}

.security-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  background: #f0f5ff;
  border: 1px solid #adc6ff;
  border-radius: 6px;
  color: #1d39c4;
  font-size: 13px;
  margin-top: 8px;
}

.security-notice :deep(.anticon) {
  font-size: 16px;
  margin-top: 2px;
}
</style>
